import React, { useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import { HistoryIcon } from '@/shared/ui/display/EmojiIcon';
import { EventCardFields } from './EventCardFields';
import { useSessionTimelineTheme } from '../theme';
import type { EventField, FieldHistory } from '../types/sessionTimeline.types';

interface EventCardContentProps {
  content?: React.ReactNode;
  fields?: readonly EventField[];
  /** 히스토리 모달용 (있으면 필드 영역 우상단에 히스토리 아이콘 표시) */
  fieldHistory?: FieldHistory;
  /** 히스토리 클릭 시 전달할 액션 ID */
  historyActionId?: string;
  sessionId?: string;
  onActionClick?: (actionId: string, sessionId: string) => void;
}

export function EventCardContent({
  content,
  fields,
  fieldHistory,
  historyActionId,
  sessionId,
  onActionClick,
}: EventCardContentProps) {
  const { tokens } = useSessionTimelineTheme();

  const hasHistory = fields && fields.length > 0 && fieldHistory && historyActionId && sessionId;

  const handleHistoryClick = useCallback(() => {
    if (sessionId && historyActionId) {
      onActionClick?.(historyActionId, sessionId);
    }
  }, [sessionId, historyActionId, onActionClick]);

  return (
    <Box
      sx={{
        marginTop: 2,
        padding: 2,
        paddingTop: hasHistory ? 1 : 2,
        backgroundColor: 'action.hover',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      {hasHistory && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <IconButton
            size="small"
            onClick={handleHistoryClick}
            sx={{
              padding: '4px',
              color: tokens.action.moreButton.color,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            aria-label="변경 이력"
          >
            <HistoryIcon sx={{ fontSize: tokens.action.moreButton.size }} />
          </IconButton>
        </Box>
      )}
      {content && (
        <Box
          sx={{
            marginBottom: fields && fields.length > 0 ? 2 : 0,
            fontSize: '14px',
            color: 'text.primary',
            lineHeight: 1.7,
            whiteSpace: 'pre-line',
          }}
        >
          {content}
        </Box>
      )}
      {fields && fields.length > 0 && <EventCardFields fields={fields} />}
    </Box>
  );
}
